import { ChangeDetectorRef, Component, forwardRef, inject, input, model } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HlmIconComponent } from './icon.component';
import { cn } from './cn';

/**
 * Checkbox — works with ngModel, formControlName, or two-way binding on `checked`.
 *
 *   <hlm-checkbox [(checked)]="isActive" label="Active" />
 *   <hlm-checkbox formControlName="active">Active</hlm-checkbox>
 */
@Component({
  selector: 'hlm-checkbox',
  standalone: true,
  imports: [HlmIconComponent],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HlmCheckboxComponent), multi: true },
  ],
  template: `
    <label [class]="_rootClass()">
      <span [class]="_boxClass()">
        @if (checked()) { <hlm-icon name="lucideCheck" size="0.8rem" class="text-primary-foreground" /> }
      </span>
      <span class="text-base leading-none text-foreground">
        @if (label()) { {{ label() }} }
        <ng-content />
      </span>
      <input
        type="checkbox"
        class="sr-only"
        [checked]="checked()"
        [disabled]="disabled()"
        (change)="_toggle($event)"
        (blur)="onTouched()"
      />
    </label>
  `,
})
export class HlmCheckboxComponent implements ControlValueAccessor {
  readonly checked = model(false);
  readonly disabled = model(false);
  readonly label = input<string | null>(null);
  readonly userClass = input<string>('', { alias: 'class' });

  private cdr = inject(ChangeDetectorRef);
  private onChange: (v: boolean) => void = () => {};
  onTouched: () => void = () => {};

  protected readonly _rootClass = () =>
    cn(
      'inline-flex items-center gap-2.5 cursor-pointer select-none',
      this.disabled() && 'opacity-60 cursor-not-allowed',
      this.userClass(),
    );

  protected readonly _boxClass = () =>
    cn(
      'grid h-4 w-4 flex-none place-items-center rounded border transition-colors',
      this.checked()
        ? 'border-primary bg-primary'
        : 'border-border bg-background hover:border-foreground/40',
    );

  _toggle(e: Event): void {
    if (this.disabled()) return;
    const v = (e.target as HTMLInputElement).checked;
    this.checked.set(v);
    this.onChange(v);
  }

  writeValue(v: boolean): void { this.checked.set(!!v); this.cdr.markForCheck(); }
  registerOnChange(fn: (v: boolean) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled.set(d); }
}
